<template>
  <div class="mb-3 pb-2" v-if="modelValue?.length">
    <div v-if="serviceHours && adjustedServiceHours" class="sub-phase-percentage-list">
      <div v-for="(group, index) in percentageGroups" :key="index" class="sub-phase-percentage-row">
        <div class="flex min-w-0 items-center gap-2.5">
          <div class="service-effort-icon-badge !mb-0 shrink-0">
            <component :is="group.icon" class="h-5 w-5 text-neutral-550" stroke="1.8" />
          </div>
          <div class="truncate text-base text-neutral-550">{{ group.label }}</div>
        </div>

        <div class="flex flex-col items-end text-sm">
          <span class="text-black">{{ formatServiceHours(serviceHours[index], 10) }} h</span>
          <span v-if="modelValue[index] !== 0" class="text-primary-dark">
            {{ formatServiceHours(adjustedServiceHours[index], 10) }} h
          </span>
          <span v-else class="text-neutral-550 opacity-70">0 h</span>
        </div>

        <div class="sub-phase-percentage-controls flex items-center justify-end text-center">
          <button
            type="button"
            class="adjust-value-button"
            :aria-label="`${group.label} -1%`"
            @click="adaptValue(index, -1)">
            <IconMinus class="h-4 w-4" stroke="2" />
          </button>
          <div class="flex items-center">
            <input
              :value="getRoundedPercentageDisplay(modelValue[index])"
              type="number"
              min="-100"
              max="100"
              @input="onPercentageInput(index, $event)"
              @keyup="imposeMinMax(index)"
              @change="imposeMinMax(index)"
              @blur="imposeMinMax(index)"
              class="core-form-input mb-0 w-14 text-center text-primary-dark md:w-16" />
            <span class="ml-1 text-sm text-neutral-550">%</span>
          </div>
          <button
            type="button"
            class="adjust-value-button"
            :aria-label="`${group.label} +1%`"
            @click="adaptValue(index, 1)">
            <IconPlus class="h-4 w-4" stroke="2" />
          </button>
        </div>
      </div>
      <div class="sub-phase-percentage-total-row">
        <div class="flex min-w-0 items-center gap-2.5">
          <div class="service-effort-icon-badge !mb-0 shrink-0">
            <IconSum class="h-5 w-5 text-neutral-650" stroke="1.8" />
          </div>
          <span class="truncate text-base text-neutral-650">{{ wt('effort', 'totalProjectPlanning') }}</span>
        </div>
        <div class="flex flex-col items-end text-sm">
          <span class="text-base text-black">{{ formatServiceHours(serviceHoursSum) }} h</span>
        </div>
        <div class="sub-phase-percentages-total-spacer" aria-hidden="true"></div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { deepClone } from '@/helpers/objects_arrays'
import {
  IconAdjustments,
  IconCalendar,
  IconCurrencyDollar,
  IconEye,
  IconMinus,
  IconPlus,
  IconSum,
  IconUsersGroup,
} from '@tabler/icons-vue'
import { formatHours } from '@/helpers/format'
import { computed, toRef, type PropType } from 'vue'
import { useWidgetText } from '@/helpers/useWidgetText'

const modelValue = defineModel({ type: Object as PropType<number[]> })

const props = defineProps<{
  formErrorPrefix: string
  serviceHours: number[] | null
  adjustedServiceHours: number[] | null
  serviceHoursSum: number
  role?: string
}>()

const { wt } = useWidgetText('widget-texts-project-service', toRef(props, 'role'))

const percentageGroups = computed(() => [
  {
    label: wt('effort', 'percentageGroups', 'option', 'organization'),
    icon: IconUsersGroup,
  },
  {
    label: wt('effort', 'percentageGroups', 'option', 'coreTasks'),
    icon: IconEye,
  },
  {
    label: wt('effort', 'percentageGroups', 'option', 'costing'),
    icon: IconCurrencyDollar,
  },
  {
    label: wt('effort', 'percentageGroups', 'option', 'scheduling'),
    icon: IconCalendar,
  },
  {
    label: wt('effort', 'percentageGroups', 'option', 'coordination'),
    icon: IconAdjustments,
  },
])

const formatServiceHours = function (hours: number, threshold: number = 100): string {
  return formatHours(hours, threshold)
}

const getRoundedPercentageDisplay = function (value?: number): string {
  return value == null ? '' : String(Math.round(value))
}

const onPercentageInput = function (index: number, event: Event): void {
  if (!modelValue.value) return

  const target = event.target as HTMLInputElement
  if (target.value === '') {
    modelValue.value[index] = 0
    return
  }

  const parsed = Number(target.value)
  if (!Number.isFinite(parsed)) return

  modelValue.value[index] = parsed
}

const adaptValue = function (index: number, value: number): void {
  if (!modelValue.value) return
  const modelValueClone = deepClone(modelValue.value)
  modelValueClone[index] += value
  if (modelValueClone[index] < -100) {
    modelValueClone[index] = -100
  } else if (modelValueClone[index] > 100) {
    modelValueClone[index] = 100
  }
  modelValue.value = modelValueClone
}

const imposeMinMax = function (index: number) {
  if (!modelValue.value || modelValue.value[index] == undefined) return

  const value = modelValue.value[index]
  if (!Number.isFinite(value)) {
    modelValue.value[index] = 0
    return
  }
  if (value > 100) {
    modelValue.value[index] = 100
  }
  if (value < -100) {
    modelValue.value[index] = -100
  }
}
</script>

<style lang="postcss" scoped>
.sub-phase-percentage-list {
  @apply overflow-hidden border-y border-neutral-300 bg-white;
}
.sub-phase-percentage-row {
  @apply grid items-center gap-3 px-3 py-2;
  grid-template-columns: minmax(0, 1fr) auto auto;
  @apply border-b border-neutral-300 last:border-b-0;
}
.sub-phase-percentage-total-row {
  @apply grid items-center gap-3 border-x border-neutral-300 bg-neutral-35 px-3 py-2.5;
  grid-template-columns: minmax(0, 1fr) auto auto;
}
.sub-phase-percentage-controls {
  @apply w-32 md:w-36 lg:w-40;
}
.sub-phase-percentages-total-spacer {
  @apply w-32 md:w-36 lg:w-40;
}
.adjust-value-button {
  @apply inline-flex h-10 w-8 cursor-pointer items-center justify-center text-neutral-550 transition-colors hover:text-black lg:w-10;
}

.service-effort-icon-badge {
  @apply mb-2 flex items-center justify-center;
}

/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type='number'] {
  -moz-appearance: textfield;
}
</style>
